/**
 * @fileoverview Local Test Runner for single-browser testing
 *
 * Runs integration tests locally without Firebase coordination.
 * Useful for development and quick validation of game mechanics.
 */

import { ZodiacEngine } from "../zodiacEngine/zodiacEngine";
import { Mode, State, MoveType, IPiece } from "../zodiacEngine/types";
import { IntegrationTest, TestResult, ParsedMove } from "./types";
import { parseMove } from "./MoveNotationParser";
import { detectResult } from "./ResultDetector";
import { ZodiacFEN } from "../zodiacEngine/Fen/ZodiacFEN";
import { inferMoveFromFENDelta } from "../zodiacEngine/Fen/fenDeltaUtils";
import TimeControlManager from "../timeControl/TimeControlManager";

/**
 * Individual step in test execution
 */
export interface TestStep {
  name: string;
  status: "pending" | "running" | "passed" | "failed" | "skipped";
  message?: string;
  timestamp?: number;
  duration?: number;
}

/**
 * Delta information showing what changed
 */
export interface TestDelta {
  pieceMoved?: string;        // "Light Bear A1->B2"
  pieceCaptured?: string;     // "Dark Frog at B2"
  statusApplied?: string[];   // ["Poison applied to Light Bear", "Entangle applied to Dark Goat"]
  statusChanged?: boolean;    // true if any status effect was added/removed
  bonusTurn?: boolean;
  turnChanged?: boolean;
  gameEnded?: boolean;
}

/**
 * Result from a single step in a sequence test
 */
export interface SequenceStepResult {
  stepNumber: number;
  move: string;
  note?: string;
  expectedFEN: string;
  actualFEN: string;
  fenMatches: boolean;
  expectedDelta?: TestDelta;
  actualDelta?: TestDelta;
  deltaMatches: boolean;
  expectedStatus?: string[]; // e.g. ["Light Monkey: Poisoned"]
  actualStatus?: string[]; // Extracted from actual FEN
  expectedResult?: TestResult; // Expected game end type
  actualResult?: TestResult;   // Actual game end type
  passed: boolean;
  error?: string;
}

export interface LocalTestResult {
  test: IntegrationTest;
  passed: boolean;
  actualFEN: string;
  expectedFEN: string;
  actualResult: TestResult;
  expectedResult: TestResult | undefined;
  error?: string;
  duration: number;

  // Detailed step tracking
  steps: TestStep[];

  // Delta information
  expectedDelta?: TestDelta;
  actualDelta?: TestDelta;

  // Sequence test results (for multi-step tests)
  isSequence?: boolean;
  sequenceSteps?: SequenceStepResult[];

  // Status information (e.g. ["Light Rhino: Infected", "Dark Bear: Zombified"])
  expectedStatus?: string[];
  actualStatus?: string[];

  // Piece status info (runtime properties like isShadow, isShadowed)
  pieceStatusInfo?: string[];

  // Additional debug info
  inputFENVerified: boolean;
  moveNotationParsed: boolean;
  moveExecuted: boolean;
  fenMatches: boolean;
  resultMatches: boolean;
  deltaMatches: boolean;
}

export interface TestSuiteResult {
  suiteName: string;
  results: LocalTestResult[];
  passed: number;
  failed: number;
  duration: number;
}

/**
 * Run a single test locally with detailed step tracking
 */
export async function runLocalTest(test: IntegrationTest): Promise<LocalTestResult> {
  // Check if this is a sequence test
  if (test.isSequence && test.steps && test.steps.length > 0) {
    return runSequenceTest(test);
  }

  const startTime = performance.now();
  // Reset timer singleton so stale timeExpired state from a previous test (e.g. timeout test) doesn't leak
  TimeControlManager.reset();
  const engine = new ZodiacEngine();

  // Initialize steps
  const steps: TestStep[] = [
    { name: "Initialize Engine", status: "pending" },
    { name: "Setup FEN on Board", status: "pending" },
    { name: "Set Kings", status: "pending" },
    { name: "Parse Move Notation", status: "pending" },
    { name: "Execute Move", status: "pending" },
    { name: "Compare FEN", status: "pending" },
    { name: "Check Game End", status: "pending" },
    { name: "Compare Delta", status: "pending" },
  ];

  // Helper to update step status
  const updateStep = (index: number, status: TestStep["status"], message?: string) => {
    steps[index].status = status;
    steps[index].timestamp = performance.now();
    if (message) steps[index].message = message;
  };

  // Result template
  const createResult = (
    passed: boolean,
    actualFEN: string,
    actualResult: TestResult,
    error?: string,
    extras?: Partial<LocalTestResult>
  ): LocalTestResult => ({
    test,
    passed,
    actualFEN,
    expectedFEN: test.expectedFEN,
    actualResult,
    expectedResult: test.expectedResult,
    error,
    duration: performance.now() - startTime,
    steps,
    expectedDelta: test.expectedDelta,
    inputFENVerified: false,
    moveNotationParsed: false,
    moveExecuted: false,
    fenMatches: false,
    resultMatches: false,
    deltaMatches: false,
    ...extras,
  });

  try {
    // Step 1: Initialize engine
    updateStep(0, "running");
    engine.setGameMode(Mode.TestAI);
    updateStep(0, "passed", "Engine initialized in TestAI mode");

    // Step 2: Setup the board from FEN
    updateStep(1, "running");
    engine.loadFromFEN(test.inputFEN);
    const loadedFEN = ZodiacFEN.encode(engine.getGameState());
    const inputFENVerified = normalizeFEN(loadedFEN).includes(normalizeFEN(test.inputFEN).substring(0, 10));
    updateStep(1, inputFENVerified ? "passed" : "failed", `Loaded: ${loadedFEN}`);

    // Step 3: Set kings and force into playing state
    updateStep(2, "running");
    // CRITICAL: Use gameStateManager.getGameState() to get the ACTUAL reference
    // engine.getGameState() returns a SHALLOW COPY, so modifications don't persist!
    const gameState = (engine as any).gameStateManager.getGameState();

    // Directly set kings on gameState (bypassing state validation)
    if (test.lightKing) {
      gameState.lightKing = test.lightKing;
    }
    if (test.darkKing) {
      gameState.darkKing = test.darkKing;
    }

    // Force into playing state
    gameState.state = State.Playing;
    gameState.turnCount = 10; // Skip placement phase
    // CRITICAL: Set mode to TestAI to prevent double turn increment
    // loadFromFEN may leave mode in Dev which triggers extra increment
    // TestAI mode: proper turn counting + no Firebase sync required
    gameState.mode = Mode.TestAI;
    updateStep(2, "passed", `Light: ${test.lightKing}, Dark: ${test.darkKing}`);

    // Step 4: Parse the move
    updateStep(3, "running");
    const parsedMove = parseMove(test.move);
    if (!parsedMove) {
      updateStep(3, "failed", `Invalid notation: ${test.move}`);
      // Skip remaining steps
      for (let i = 4; i < steps.length; i++) updateStep(i, "skipped");
      return createResult(false, test.inputFEN, { gameEnd: false }, `Failed to parse move: ${test.move}`, {
        inputFENVerified,
        moveNotationParsed: false,
      });
    }
    updateStep(3, "passed", `${parsedMove.color} ${parsedMove.pieceType} ${test.move}`);
    const moveNotationParsed = true;

    // Set correct turn on the actual game state reference (not a copy)
    gameState.isLightTurn = parsedMove.color === "light";

    // Step 5: Execute the move
    updateStep(4, "running");
    const beforeState = JSON.parse(JSON.stringify(engine.getGameState()));
    const beforeFEN = ZodiacFEN.encode(engine.getGameState());
    const moveResult = await executeMove(engine, parsedMove);

    if (!moveResult.success) {
      updateStep(4, "failed", moveResult.error);
      // Skip remaining steps
      for (let i = 5; i < steps.length; i++) updateStep(i, "skipped");
      return createResult(false, ZodiacFEN.encode(engine.getGameState()), { gameEnd: false }, moveResult.error, {
        inputFENVerified,
        moveNotationParsed,
        moveExecuted: false,
      });
    }
    updateStep(4, "passed", "Move executed successfully");
    const moveExecuted = true;

    // Get result state
    const afterState = engine.getGameState();
    const actualFEN = ZodiacFEN.encode(afterState);
    const actualResult = detectResult(beforeState, afterState, parsedMove);

    // Build delta information using real FEN delta inference
    const actualDelta = buildDelta(beforeFEN, actualFEN, beforeState, afterState, parsedMove);

    // Extract actual status from FEN + runtime piece status (isShadow, isShadowed)
    const fenStatus = extractStatusFromFEN(actualFEN);
    const runtimeStatus = extractPieceStatusInfo(afterState.board);
    const actualStatus = [...fenStatus, ...runtimeStatus];
    const pieceStatusInfo = runtimeStatus; // Keep for backward compatibility

    // Step 6: Compare FEN
    updateStep(5, "running");
    const fenMatches = normalizeFEN(actualFEN) === normalizeFEN(test.expectedFEN);
    updateStep(5, fenMatches ? "passed" : "failed", fenMatches ? "FEN matches" : `Expected: ${test.expectedFEN}, Got: ${actualFEN}`);

    // Step 7: Check game end
    updateStep(6, "running");
    const resultMatches = compareResults(test.expectedResult, actualResult);
    const expectedEnd = test.expectedResult?.gameEnd || "none";
    const actualEnd = actualResult.gameEnd || "none";
    updateStep(6, resultMatches ? "passed" : "failed", `Expected: ${expectedEnd}, Got: ${actualEnd}`);

    // Step 8: Compare Delta (if expectedDelta is provided)
    updateStep(7, "running");
    const deltaMatches = compareDelta(test.expectedDelta, actualDelta);
    if (test.expectedDelta) {
      updateStep(7, deltaMatches ? "passed" : "failed", deltaMatches ? "Delta matches" : "Delta mismatch");
    } else {
      updateStep(7, "passed", "No expected delta specified (skipped)");
    }

    const passed = fenMatches && resultMatches && deltaMatches;

    return createResult(
      passed,
      actualFEN,
      actualResult,
      !fenMatches
        ? `FEN mismatch`
        : !resultMatches
        ? `Result mismatch: expected "${expectedEnd}", got "${actualEnd}"`
        : !deltaMatches
        ? `Delta mismatch`
        : undefined,
      {
        inputFENVerified,
        moveNotationParsed,
        moveExecuted,
        fenMatches,
        resultMatches,
        deltaMatches,
        actualDelta,
        expectedStatus: test.expectedStatus,
        actualStatus,
        pieceStatusInfo,
      }
    );
  } catch (error) {
    // Mark all pending steps as skipped
    steps.forEach((step, i) => {
      if (step.status === "pending" || step.status === "running") {
        updateStep(i, "failed", error instanceof Error ? error.message : String(error));
      }
    });

    return createResult(
      false,
      "",
      { gameEnd: false },
      error instanceof Error ? error.message : String(error),
      {
        inputFENVerified: false,
        moveNotationParsed: false,
        moveExecuted: false,
        fenMatches: false,
        resultMatches: false,
        deltaMatches: false,
      }
    );
  }
}

/**
 * Build delta information from before/after states using the real FEN delta system
 */
function buildDelta(beforeFEN: string, afterFEN: string, beforeState: any, afterState: any, parsedMove?: ParsedMove): TestDelta {
  const delta: TestDelta = {};

  // Handle special commands (timeout)
  if (parsedMove?.isSpecialCommand && parsedMove.specialCommand === "timeout") {
    const loser = parsedMove.color === "light" ? "Light" : "Dark";
    delta.pieceMoved = `${loser} ran out of time`;
    delta.turnChanged = false;
    delta.gameEnded = true;
    delta.bonusTurn = false;
    delta.statusChanged = false;
    return delta;
  }

  // Use the real inferMoveFromFENDelta function from the engine
  // This is the same function used by Move History
  const wasLightMove = beforeState.isLightTurn;
  const moveDescription = inferMoveFromFENDelta(beforeFEN, afterFEN, wasLightMove);

  delta.pieceMoved = moveDescription;

  // Check for captures - if piece count decreased
  const beforePieceCount = countPieces(beforeState.board);
  const afterPieceCount = countPieces(afterState.board);
  if (afterPieceCount < beforePieceCount) {
    // The moveDescription from inferMoveFromFENDelta already includes capture info
    // We can extract it or leave pieceCaptured empty since it's in pieceMoved
    delta.pieceCaptured = undefined; // Capture info is in pieceMoved description
  }

  // Check turn change
  delta.turnChanged = beforeState.isLightTurn !== afterState.isLightTurn;

  // Check for game end (includes wins, draws, and terminations)
  // Also check lightWon since state might not be updated to PostGame yet
  delta.gameEnded = afterState.state === State.PostGame ||
                    afterState.state === State.Terminated ||
                    afterState.state === State.Draw ||
                    afterState.lightWon !== undefined;

  // Check for bonus turn (same player still has turn)
  // CRITICAL: If game ended, it's NOT a bonus turn - the turn simply didn't change because the game is over
  delta.bonusTurn = (beforeState.isLightTurn === afterState.isLightTurn) && !delta.gameEnded;

  // Check for status changes by comparing status symbols in FEN
  // Status symbols: !, ☣ (poison), ⛓ (entangle), z, ☠ (zombify), # (meadow), $ (faint/special), * ✳️ (ability)
  const beforeStatus = countStatusSymbols(beforeFEN);
  const afterStatus = countStatusSymbols(afterFEN);
  delta.statusChanged = beforeStatus !== afterStatus;

  // Status effects would be detected from the moveDescription if present
  delta.statusApplied = undefined;

  return delta;
}

/**
 * Extract status effects from FEN notation
 * Returns human-readable status strings like ["Light Monkey: Poisoned", "Dark Bear: Zombified"]
 */
function extractStatusFromFEN(fen: string): string[] {
  const statuses: string[] = [];

  // Remove turn indicator if present
  let fenString = fen.startsWith("d") ? fen.substring(1) : fen;

  // Split into columns (A-E)
  const columns = fenString.split("/");
  const columnNames = ["A", "B", "C", "D", "E"];

  for (let colIndex = 0; colIndex < columns.length; colIndex++) {
    const column = columns[colIndex];
    let rowIndex = 0; // Start from row 1 (bottom)

    for (let i = 0; i < column.length; i++) {
      const char = column[i];

      // Skip numbers (empty squares)
      if (!isNaN(parseInt(char))) {
        rowIndex += parseInt(char);
        continue;
      }

      // Skip '+' (piece stacking)
      if (char === '+') continue;

      // Check if this is a piece character
      const upperChar = char.toUpperCase();
      if ("BCFUGMOPRSTWV".includes(upperChar)) {
        // Found a piece, now check for status symbols
        const isLight = char === upperChar;
        const color = isLight ? "Light" : "Dark";
        const pieceName = getPieceName(upperChar);
        const position = `${columnNames[colIndex]}${rowIndex + 1}`;

        // Look ahead for status symbols
        let j = i + 1;
        const statusSymbols: string[] = [];
        while (j < column.length) {
          const nextChar = column[j];

          // Status symbols
          if (nextChar === '⛓') statusSymbols.push("Entangled");
          else if (nextChar === '☣' || nextChar === '!') statusSymbols.push("Poisoned");
          else if (nextChar === '⚘' || nextChar === '#') statusSymbols.push("Meadowed");
          else if (nextChar === 'z' || nextChar === '☠') statusSymbols.push("Zombified");
          else if (nextChar === '✳️' || nextChar === '*') statusSymbols.push("Ability Ready");
          else if (nextChar === '$') {
            // Special state - depends on piece type
            if (upperChar === 'W') statusSymbols.push("Lone");
            else if (upperChar === 'G') statusSymbols.push("Fainted");
            else if (upperChar === 'M') statusSymbols.push("Riding");
            else if (upperChar === 'O') statusSymbols.push("Infected");
            else statusSymbols.push("Special State");
          }
          else if (nextChar === '○') statusSymbols.push("Unmoved");
          else if (nextChar === '♛' || nextChar === '♕') statusSymbols.push("King");
          // Skip directional symbols for Penguin
          else if ("↑↗→↘↓↙←↖".includes(nextChar)) { j++; continue; }
          // If we hit another piece or number, we're done with this piece
          else if ("BCFUGMOPRSTWV".toUpperCase().includes(nextChar.toUpperCase()) || !isNaN(parseInt(nextChar))) {
            break;
          }

          j++;
        }

        // Add status for this piece (only if it has relevant status effects)
        // Filter out King and Ability Ready and Unmoved as they're not "status effects" for testing purposes
        const relevantStatuses = statusSymbols.filter(s =>
          !["King", "Ability Ready", "Unmoved"].includes(s)
        );

        if (relevantStatuses.length > 0) {
          statuses.push(`${color} ${pieceName}: ${relevantStatuses.join(", ")}`);
        }

        rowIndex++;
      }
    }
  }

  return statuses;
}

/**
 * Get piece name from character
 */
function getPieceName(char: string): string {
  const upperChar = char.toUpperCase();
  switch (upperChar) {
    case 'B': return 'Bear';
    case 'C': return 'Crane';
    case 'F': return 'Frog';
    case 'U': return 'Fungi';
    case 'G': return 'Goat';
    case 'M': return 'Monkey';
    case 'O': return 'Octopus';
    case 'P': return 'Penguin';
    case 'R': return 'Rhino';
    case 'S': return 'Snake';
    case 'T': return 'Tree';
    case 'W': return 'Wolf';
    case 'V': return 'Virus';
    default: return 'Unknown';
  }
}

/**
 * Count status symbols in FEN string
 */
function countStatusSymbols(fen: string): number {
  // Count all status effect symbols
  // !☣☠ = poison, ⛓ = entangle, z = zombify, # = meadow (ascii), ⚘ = meadow (unicode), $ = faint/special, *✳️ = ability unused
  // +v/+V = virus infection
  // NOTE: + (piece stacking) is NOT counted UNLESS it's followed by v or V (virus infection)
  const statusRegex = /[!☣⛓z☠#$*✳️⚘]/g;
  const matches = fen.match(statusRegex);
  let count = matches ? matches.length : 0;

  // Count virus infections (+v or +V)
  const virusInfectionRegex = /\+[vV]/g;
  const virusMatches = fen.match(virusInfectionRegex);
  count += virusMatches ? virusMatches.length : 0;

  return count;
}

/**
 * Count total pieces on board
 */
function countPieces(board: any[][]): number {
  let count = 0;
  for (const row of board) {
    for (const square of row) {
      count += square.Pieces?.length || 0;
    }
  }
  return count;
}

/**
 * Extract runtime piece status info (isShadow, isShadowed) from board
 * These are runtime properties NOT encoded in FEN
 */
function extractPieceStatusInfo(board: any[][]): string[] {
  const statusInfo: string[] = [];
  const columnNames = ["A", "B", "C", "D", "E"];

  for (let row = 0; row < board.length; row++) {
    for (let col = 0; col < board[row].length; col++) {
      const square = board[row][col];
      if (!square.Pieces) continue;

      for (const piece of square.Pieces) {
        const color = piece.isLight ? "Light" : "Dark";
        const position = `${columnNames[col]}${row + 1}`;
        const statuses: string[] = [];

        // Check for shadow status (fake shadow piece from octopus ink)
        if (piece.isShadow) {
          statuses.push("isShadow=true (fake shadow)");
        }
        // Check for shadowed status (real octopus that used ink)
        if (piece.isShadowed) {
          statuses.push("isShadowed=true (real octopus, inked)");
        }

        if (statuses.length > 0) {
          statusInfo.push(`${color} ${piece.type} at ${position}: ${statuses.join(", ")}`);
        }
      }
    }
  }

  return statusInfo;
}

/**
 * Run all tests in a suite
 */
export async function runTestSuite(suiteName: string, tests: IntegrationTest[]): Promise<TestSuiteResult> {
  const startTime = performance.now();
  const results: LocalTestResult[] = [];

  for (const test of tests) {
    results.push(await runLocalTest(test));
  }

  return {
    suiteName,
    results,
    passed: results.filter((r) => r.passed).length,
    failed: results.filter((r) => !r.passed).length,
    duration: performance.now() - startTime,
  };
}

/**
 * Run all test suites
 */
export async function runAllTests(
  suites: { name: string; tests: IntegrationTest[] }[]
): Promise<TestSuiteResult[]> {
  const results: TestSuiteResult[] = [];
  for (const suite of suites) {
    results.push(await runTestSuite(suite.name, suite.tests));
  }
  return results;
}

/**
 * Execute a parsed move on the engine
 */
async function executeMove(
  engine: ZodiacEngine,
  parsedMove: ParsedMove
): Promise<{ success: boolean; error?: string }> {
  // Handle special commands
  if (parsedMove.isSpecialCommand && parsedMove.specialCommand === "timeout") {
    // Use the engine's triggerTimeout method to set the actual game state
    engine.triggerTimeout(parsedMove.color);
    return { success: true };
  }

  const gameState = engine.getGameState();
  const board = gameState.board;

  // Find the piece at the from position
  const fromSquare = board[parsedMove.fromPosition.row]?.[parsedMove.fromPosition.col];
  if (!fromSquare) {
    return { success: false, error: `Invalid from position: ${parsedMove.fromPosition.row},${parsedMove.fromPosition.col}` };
  }

  // Find the piece - handle zombified pieces that act as the opposite color
  const piece = fromSquare.Pieces.find((p) => {
    if (p.type !== parsedMove.pieceType) return false;

    // Calculate effective color (zombified pieces act as opposite color)
    const effectiveIsLight = p.zombifiedStatus?.isZombified
      ? !p.isLight
      : p.isLight;

    return effectiveIsLight === (parsedMove.color === "light");
  });

  if (!piece) {
    return {
      success: false,
      error: `No ${parsedMove.color} ${parsedMove.pieceType} found at ${parsedMove.fromPosition.row},${parsedMove.fromPosition.col}`,
    };
  }

  // Get valid moves for this piece
  const piecePosition = { row: parsedMove.fromPosition.row, col: parsedMove.fromPosition.col };
  const validMoves = engine.getValidMoves(piecePosition, piece);

  // Find matching move
  let targetMove = validMoves.find((m) => {
    // For entangle moves (noMove, tripleentangle, or split entangle with direction)
    // We need to find a move that has an entangle side effect at that position
    if (parsedMove.moveType === MoveType.noMove || parsedMove.moveType === MoveType.tripleentangle || parsedMove.entangleDirection) {
      const matchingEntangleSE = m.sideEffects?.find((se) => {
        const posMatches =
          se.position.row === parsedMove.toPosition.row &&
          se.position.col === parsedMove.toPosition.col;

        // If direction is specified, also match direction (for split entangle disambiguation)
        if (parsedMove.entangleDirection && (se as any).direction) {
          return posMatches && (se as any).direction === parsedMove.entangleDirection;
        }

        return posMatches;
      });
      // Split entangle uses MoveType.regular, so also allow that
      if (matchingEntangleSE && (m.moveType === MoveType.noMove || m.moveType === MoveType.regular)) {
        return true;
      }
    }

    const toMatches =
      m.position.row === parsedMove.toPosition.row && m.position.col === parsedMove.toPosition.col;

    // If move type is specified, check it
    if (parsedMove.moveType !== MoveType.regular) {
      return toMatches && m.moveType === parsedMove.moveType;
    }

    return toMatches;
  });

  if (!targetMove) {
    // List available moves for debugging
    const availableMoves = validMoves.map(
      (m) => {
        const sideEffectPositions = m.sideEffects?.map((se) => `SE@${se.position.row},${se.position.col}`).join(";") || "";
        return `${m.position.row},${m.position.col}:${m.moveType}${sideEffectPositions ? ` [${sideEffectPositions}]` : ""}`;
      }
    );
    return {
      success: false,
      error: `No valid move to ${parsedMove.toPosition.row},${parsedMove.toPosition.col}. Available: [${availableMoves.join(", ")}]`,
    };
  }

  // Execute the move
  try {
    // Get target piece if this is a capture or infection
    const toSquare = board[parsedMove.toPosition.row]?.[parsedMove.toPosition.col];
    let targetPiece: IPiece | null = null;

    if (targetMove.moveType === MoveType.infect) {
      // Virus can infect ANY animal piece (friendly or enemy)
      targetPiece = toSquare?.Pieces[0] || null;
    } else if (targetMove.moveType === MoveType.lane || targetMove.moveType === MoveType.house) {
      // Lane and house moves target ally pieces
      targetPiece = toSquare?.Pieces.find((p) => p.isLight === (parsedMove.color === "light")) || null;
    } else {
      // For other moves, get enemy piece
      targetPiece = toSquare?.Pieces.find((p) => p.isLight !== (parsedMove.color === "light")) || null;
    }

    // makeMove is async but we don't need to wait for side effects in testing
    await engine.makeMove(
      piecePosition,
      targetMove.position,
      piece,
      targetPiece,
      targetMove.moveType,
      targetMove.sideEffects || []
    );
    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: `Move execution failed: ${error instanceof Error ? error.message : String(error)}`,
    };
  }
}

/**
 * Normalize FEN for comparison (handle minor formatting differences)
 */
function normalizeFEN(fen: string): string {
  // Remove trailing slashes and whitespace
  let normalized = fen.trim().replace(/\/+$/, "");

  // Strip turn indicator prefix (d or l at the start)
  // The FEN encoder adds 'd' for dark turn, 'l' for light turn
  if (normalized.startsWith('d') || normalized.startsWith('l')) {
    normalized = normalized.substring(1);
  }

  // Strip special ability symbols (keep piece logic but ignore ability state)
  // ✳️ or * = Special single-use ability (Frog lilypad, Rhino charge, Snake shed)
  // ○ = Rhino unmoved (lane ability available)
  // $ = Special state (Lone wolf, Shadowed octopus, Fainted goat, High monkey)
  // ⛓ = Entangled
  // ☠ or ! or ☣ = Poisoned (multiple representations for backwards compatibility)
  // # = Meadowed
  // z = Zombified
  // Direction arrows (↑ ↓ ← → ↖ ↗ ↘ ↙) for Penguin facing
  normalized = normalized.replace(/[✳️*○$⛓☠!☣#z↑↓←→↖↗↘↙]/g, "");

  // Normalize king symbols to K/k notation
  // ♛ (dark king marker) -> the piece letter becomes uppercase with K suffix
  // ♕ (light king marker) -> the piece letter becomes uppercase with K suffix
  // In expected FEN: BK = Bear King (light), ck = Crane King (dark)
  // In actual FEN: B♛ = Bear King (light), c♕ = Crane King (dark)
  // Wait - the symbols are backwards in actual output. Let's handle both cases:
  normalized = normalized.replace(/([A-Z])♛/g, "$1K"); // Light piece with dark crown? -> Light King
  normalized = normalized.replace(/([a-z])♕/g, "$1k"); // Dark piece with light crown? -> Dark King
  normalized = normalized.replace(/([A-Z])♕/g, "$1K"); // Light piece with light crown -> Light King
  normalized = normalized.replace(/([a-z])♛/g, "$1k"); // Dark piece with dark crown -> Dark King

  return normalized;
}

/**
 * Compare expected and actual results
 */
function compareResults(expected: TestResult | undefined, actual: TestResult): boolean {
  if (!expected) {
    // No expected result means we expect no game ending
    return actual.gameEnd === false;
  }

  if (expected.gameEnd !== actual.gameEnd) {
    return false;
  }

  if (expected.winner && expected.winner !== actual.winner) {
    return false;
  }

  return true;
}

/**
 * Compare expected and actual delta
 */
function compareDelta(expected: TestDelta | undefined, actual: TestDelta): boolean {
  if (!expected) {
    // No expected delta means we skip delta comparison (always passes)
    return true;
  }

  // Compare pieceMoved
  if (expected.pieceMoved !== undefined && expected.pieceMoved !== actual.pieceMoved) {
    return false;
  }

  // Compare pieceCaptured
  if (expected.pieceCaptured !== undefined && expected.pieceCaptured !== actual.pieceCaptured) {
    return false;
  }

  // Compare statusChanged
  if (expected.statusChanged !== undefined && expected.statusChanged !== actual.statusChanged) {
    return false;
  }

  // Compare turnChanged
  if (expected.turnChanged !== undefined && expected.turnChanged !== actual.turnChanged) {
    return false;
  }

  // Compare bonusTurn
  if (expected.bonusTurn !== undefined && expected.bonusTurn !== actual.bonusTurn) {
    return false;
  }

  // Compare gameEnded
  if (expected.gameEnded !== undefined && expected.gameEnded !== actual.gameEnded) {
    return false;
  }

  // Compare statusApplied (if both are defined)
  if (expected.statusApplied !== undefined) {
    if (!actual.statusApplied) {
      return false;
    }
    if (expected.statusApplied.length !== actual.statusApplied.length) {
      return false;
    }
    for (let i = 0; i < expected.statusApplied.length; i++) {
      if (expected.statusApplied[i] !== actual.statusApplied[i]) {
        return false;
      }
    }
  }

  return true;
}

/**
 * Compare expected and actual test results (game end conditions)
 */
function compareResult(expected: TestResult | undefined, actual: TestResult): boolean {
  if (!expected) {
    // No expected result means we skip result comparison (always passes)
    return true;
  }

  // Compare gameEnd type
  if (expected.gameEnd !== actual.gameEnd) {
    return false;
  }

  // Compare winner (if gameEnd is not false)
  if (expected.gameEnd !== false) {
    if (expected.winner !== actual.winner) {
      return false;
    }
  }

  return true;
}

/**
 * Run a multi-step sequence test
 */
async function runSequenceTest(test: IntegrationTest): Promise<LocalTestResult> {
  const startTime = performance.now();
  TimeControlManager.reset();
  const engine = new ZodiacEngine();

  // Initialize high-level steps
  const steps: TestStep[] = [
    { name: "Initialize Engine", status: "pending" },
    { name: "Setup FEN on Board", status: "pending" },
    { name: "Set Kings", status: "pending" },
    { name: "Execute Sequence", status: "pending" },
  ];

  const updateStep = (index: number, status: TestStep["status"], message?: string) => {
    steps[index].status = status;
    steps[index].timestamp = performance.now();
    if (message) steps[index].message = message;
  };

  const createResult = (
    passed: boolean,
    actualFEN: string,
    error?: string,
    extras?: Partial<LocalTestResult>
  ): LocalTestResult => ({
    test,
    passed,
    actualFEN,
    expectedFEN: test.expectedFEN,
    actualResult: { gameEnd: false },
    expectedResult: test.expectedResult,
    error,
    duration: performance.now() - startTime,
    steps,
    inputFENVerified: false,
    moveNotationParsed: false,
    moveExecuted: false,
    fenMatches: false,
    resultMatches: false,
    deltaMatches: false,
    isSequence: true,
    ...extras,
  });

  try {
    // Step 1: Initialize engine
    updateStep(0, "running");
    engine.setGameMode(Mode.TestAI);
    updateStep(0, "passed", "Engine initialized in TestAI mode");

    // Step 2: Setup board
    updateStep(1, "running");
    engine.loadFromFEN(test.inputFEN);
    const loadedFEN = ZodiacFEN.encode(engine.getGameState());
    const inputFENVerified = normalizeFEN(loadedFEN).includes(normalizeFEN(test.inputFEN).substring(0, 10));
    updateStep(1, inputFENVerified ? "passed" : "failed", `Loaded: ${loadedFEN}`);

    // Step 3: Set kings
    updateStep(2, "running");
    // CRITICAL: Use gameStateManager.getGameState() to get the ACTUAL reference
    // engine.getGameState() returns a SHALLOW COPY, so modifications don't persist!
    const gameState = (engine as any).gameStateManager.getGameState();
    if (test.lightKing) gameState.lightKing = test.lightKing;
    if (test.darkKing) gameState.darkKing = test.darkKing;
    gameState.state = State.Playing;
    gameState.turnCount = 10;
    // CRITICAL: Set mode to TestAI to prevent double turn increment
    // loadFromFEN may leave mode in an unexpected state (Dev/Sandbox)
    // TestAI mode uses proper turn counting via changeTurns() only
    gameState.mode = Mode.TestAI;
    updateStep(2, "passed", `Light: ${test.lightKing}, Dark: ${test.darkKing}`);

    // Step 4: Execute sequence
    updateStep(3, "running");
    const sequenceSteps: SequenceStepResult[] = [];
    let allStepsPassed = true;
    let currentFEN = loadedFEN;

    for (let i = 0; i < test.steps!.length; i++) {
      const step = test.steps![i];
      const stepNumber = i + 1;

      // Parse and execute the move
      const parsedMove = parseMove(step.move);
      if (!parsedMove) {
        sequenceSteps.push({
          stepNumber,
          move: step.move,
          note: step.note,
          expectedFEN: step.expectedFEN,
          actualFEN: currentFEN,
          fenMatches: false,
          deltaMatches: false,
          expectedResult: step.expectedResult,
          passed: false,
          error: `Failed to parse move: ${step.move}`,
        });
        allStepsPassed = false;
        break;
      }

      // Set correct turn on the actual game state reference (not a copy)
      gameState.isLightTurn = parsedMove.color === "light";

      // Execute move
      const beforeFEN = ZodiacFEN.encode(engine.getGameState());
      const beforeState = JSON.parse(JSON.stringify(engine.getGameState()));
      const moveResult = await executeMove(engine, parsedMove);

      if (!moveResult.success) {
        sequenceSteps.push({
          stepNumber,
          move: step.move,
          note: step.note,
          expectedFEN: step.expectedFEN,
          actualFEN: currentFEN,
          fenMatches: false,
          deltaMatches: false,
          expectedResult: step.expectedResult,
          passed: false,
          error: moveResult.error,
        });
        allStepsPassed = false;
        break;
      }

      // Get actual FEN and delta
      const afterState = engine.getGameState();
      const actualFEN = ZodiacFEN.encode(afterState);
      const actualDelta = buildDelta(beforeFEN, actualFEN, beforeState, afterState, parsedMove);

      // Extract actual status from FEN + runtime piece status (isShadow, isShadowed)
      const fenStatus = extractStatusFromFEN(actualFEN);
      const runtimeStatus = extractPieceStatusInfo(afterState.board);
      const actualStatus = [...fenStatus, ...runtimeStatus];

      // Compare FEN
      const fenMatches = normalizeFEN(actualFEN) === normalizeFEN(step.expectedFEN);

      // Compare delta
      const deltaMatches = compareDelta(step.expectedDelta, actualDelta);

      // Detect actual result if game ended
      const actualResult = actualDelta.gameEnded
        ? detectResult(beforeState, afterState, parsedMove)
        : { gameEnd: false as const };

      // Compare result (game end type and winner)
      const resultMatches = compareResult(step.expectedResult, actualResult);

      const stepPassed = fenMatches && deltaMatches && resultMatches;
      if (!stepPassed) allStepsPassed = false;

      sequenceSteps.push({
        stepNumber,
        move: step.move,
        note: step.note,
        expectedFEN: step.expectedFEN,
        actualFEN,
        fenMatches,
        expectedDelta: step.expectedDelta,
        actualDelta,
        deltaMatches,
        expectedStatus: step.expectedStatus,
        actualStatus,
        expectedResult: step.expectedResult,
        actualResult,
        passed: stepPassed,
      });

      currentFEN = actualFEN;
    }

    updateStep(3, allStepsPassed ? "passed" : "failed",
      `Executed ${sequenceSteps.length}/${test.steps!.length} steps`);

    // For sequence tests, we don't need a separate final state validation
    // The final state is validated as part of the last sequence step
    const finalFEN = ZodiacFEN.encode(engine.getGameState());
    const passed = allStepsPassed;

    return createResult(passed, finalFEN, passed ? undefined : "Some sequence steps failed", {
      inputFENVerified,
      moveNotationParsed: true,
      moveExecuted: true,
      fenMatches: allStepsPassed, // All steps passed = FEN matches
      resultMatches: true,
      deltaMatches: allStepsPassed,
      sequenceSteps,
    });

  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    updateStep(3, "failed", errorMessage);
    return createResult(false, test.inputFEN, errorMessage, {
      inputFENVerified: false,
    });
  }
}
